import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import AppShell from '../components/AppShell';
import { ContentCalendarView } from '../components/ContentCalendarView';
import { 
  Calendar, 
  Clock, 
  Video, 
  Image as ImageIcon, 
  Music,
  Package,
  CheckCircle2,
  XCircle,
  CalendarDays,
  Search,
  Filter,
  ChevronRight,
  Plus,
  Edit3,
  Trash2,
  Copy,
  ExternalLink,
  Instagram,
  Youtube,
  Linkedin,
  Twitter,
  Facebook,
  MoreVertical,
  AlertCircle,
  Sparkles,
  X,
  ChevronDown,
  Settings2,
  Bell
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Platform icons mapping
const platformIcons = {
  instagram: Instagram,
  tiktok: Music, // Using Music as TikTok icon
  youtube_shorts: Youtube,
  facebook: Facebook,
  twitter: Twitter,
  linkedin: Linkedin
};

// Content type icons
const contentTypeIcons = {
  video: Video,
  image: ImageIcon,
  music: Music,
  package: Package
};

// Status colors and icons
const statusConfig = {
  scheduled: { 
    color: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    icon: Clock,
    label: 'Scheduled'
  },
  completed: { 
    color: 'bg-green-500/20 text-green-400 border-green-500/30',
    icon: CheckCircle2,
    label: 'Done'
  },
  failed: { 
    color: 'bg-red-500/20 text-red-400 border-red-500/30',
    icon: XCircle,
    label: 'Failed'
  }
};

const SchedulerPage = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  
  // State
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusCounts, setStatusCounts] = useState({ all: 0, scheduled: 0, completed: 0, failed: 0 });
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [platforms, setPlatforms] = useState([]);
  const [timezones, setTimezones] = useState([]);
  
  // Modal states
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [batchModalOpen, setBatchModalOpen] = useState(false);
  
  // Library content for scheduling
  const [libraryContent, setLibraryContent] = useState([]);
  const [loadingLibrary, setLoadingLibrary] = useState(false);
  const [selectedContentType, setSelectedContentType] = useState('video');

  // Fetch scheduled items
  const fetchScheduledItems = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (activeFilter !== 'all') params.append('status', activeFilter);
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await axios.get(`${API}/api/scheduler/?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setItems(response.data.items || []);
      setStatusCounts(response.data.status_counts || { all: 0, scheduled: 0, completed: 0, failed: 0 });
    } catch (error) {
      console.error('Failed to fetch scheduled items:', error);
      toast.error('Failed to load scheduled items');
    } finally {
      setLoading(false);
    }
  }, [token, activeFilter, searchQuery]);

  // Fetch platforms and timezones
  const fetchPlatforms = async () => {
    try {
      const response = await axios.get(`${API}/api/scheduler/platforms`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlatforms(response.data.platforms || []);
      setTimezones(response.data.timezones || []);
    } catch (error) {
      console.error('Failed to fetch platforms:', error);
    }
  };

  // Fetch library content for scheduling
  const fetchLibraryContent = async (contentType) => {
    try {
      setLoadingLibrary(true);
      const response = await axios.get(`${API}/api/library/?type=${contentType}&limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLibraryContent(response.data.items || []);
    } catch (error) {
      console.error('Failed to fetch library:', error);
      toast.error('Failed to load library content');
    } finally {
      setLoadingLibrary(false);
    }
  };

  useEffect(() => {
    fetchScheduledItems();
    fetchPlatforms();
  }, [fetchScheduledItems]);

  // Mark item as completed
  const markCompleted = async (itemId) => {
    try {
      await axios.post(`${API}/api/scheduler/${itemId}/mark_completed`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Marked as completed!');
      fetchScheduledItems();
    } catch (error) {
      console.error('Failed to mark completed:', error);
      toast.error('Failed to mark as completed');
    }
  };

  // Delete scheduled item
  const deleteItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to cancel this scheduled post?')) return;
    
    try {
      await axios.delete(`${API}/api/scheduler/${itemId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Scheduled post cancelled');
      fetchScheduledItems();
    } catch (error) {
      console.error('Failed to delete:', error);
      toast.error('Failed to cancel scheduled post');
    }
  };

  // Copy caption to clipboard
  const copyCaption = (caption) => {
    navigator.clipboard.writeText(caption);
    toast.success('Caption copied to clipboard!');
  };

  // Format date/time for display
  const formatDateTime = (isoString) => {
    const date = new Date(isoString);
    return {
      date: date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
      time: date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
    };
  };

  // Check if time is in the past
  const isPast = (isoString) => {
    return new Date(isoString) < new Date();
  };

  // Scheduled Item Card
  const ScheduledItemCard = ({ item }) => {
    const StatusIcon = statusConfig[item.status]?.icon || Clock;
    const PlatformIcon = platformIcons[item.platform] || Calendar;
    const ContentIcon = contentTypeIcons[item.content_type] || Sparkles;
    const { date, time } = formatDateTime(item.scheduled_time);
    const isOverdue = item.status === 'scheduled' && isPast(item.scheduled_time);
    
    return (
      <div 
        className={`bg-card border rounded-xl p-4 hover:border-primary/50 transition-all ${
          isOverdue ? 'border-yellow-500/50' : 'border-border'
        }`}
        data-testid={`scheduled-item-${item.id}`}
      >
        {/* Header Row */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            {/* Thumbnail or Icon */}
            {item.thumbnail_url ? (
              <div className="w-14 h-14 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                <img 
                  src={item.thumbnail_url} 
                  alt={item.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="hidden w-full h-full items-center justify-center">
                  <ContentIcon className="w-6 h-6 text-muted-foreground" />
                </div>
              </div>
            ) : (
              <div className="w-14 h-14 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                <ContentIcon className="w-6 h-6 text-muted-foreground" />
              </div>
            )}
            
            <div className="min-w-0">
              <h3 className="font-medium text-sm line-clamp-1">{item.title}</h3>
              <div className="flex items-center gap-2 mt-1">
                <span className={`text-xs px-2 py-0.5 rounded-full border ${statusConfig[item.status]?.color}`}>
                  <StatusIcon className="w-3 h-3 inline mr-1" />
                  {statusConfig[item.status]?.label}
                </span>
                {isOverdue && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
                    Overdue
                  </span>
                )}
              </div>
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex items-center gap-1">
            {item.status === 'scheduled' && (
              <>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setSelectedItem(item);
                    setEditModalOpen(true);
                  }}
                  className="h-8 w-8 p-0"
                  data-testid={`edit-${item.id}`}
                >
                  <Edit3 className="w-4 h-4" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => markCompleted(item.id)}
                  className="h-8 px-2 text-xs text-green-500 hover:text-green-400 hover:bg-green-500/10"
                  title="Mark as done"
                  data-testid={`complete-${item.id}`}
                >
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  Done
                </Button>
              </>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => deleteItem(item.id)}
              className="h-8 w-8 p-0 text-red-500 hover:text-red-400 hover:bg-red-500/10"
              data-testid={`delete-${item.id}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
        
        {/* Schedule Info */}
        <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
          <div className="flex items-center gap-1.5">
            <CalendarDays className="w-4 h-4" />
            <span>{date}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Clock className="w-4 h-4" />
            <span>{time}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <PlatformIcon className="w-4 h-4" />
            <span className="capitalize">{item.platform?.replace('_', ' ')}</span>
          </div>
        </div>
        
        {/* Caption */}
        {item.caption && (
          <div className="bg-muted/50 rounded-lg p-3 text-sm">
            <div className="flex items-start justify-between gap-2">
              <p className="text-muted-foreground line-clamp-2">{item.caption}</p>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => copyCaption(item.caption)}
                className="h-7 px-2 text-xs flex-shrink-0"
              >
                <Copy className="w-3 h-3 mr-1" />
                Copy
              </Button>
            </div>
          </div>
        )}
        
        {/* Error message for failed items */}
        {item.status === 'failed' && item.last_error && (
          <div className="mt-3 p-2 bg-red-500/10 border border-red-500/30 rounded-lg">
            <p className="text-xs text-red-400 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {item.last_error}
            </p>
          </div>
        )}
        
        {/* Notes */}
        {item.notes && (
          <div className="mt-3 text-xs text-muted-foreground">
            <span className="font-medium">Note:</span> {item.notes}
          </div>
        )}
      </div>
    );
  };

  // Empty State
  const EmptyState = () => (
    <div className="text-center py-16">
      <CalendarDays className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
      <h3 className="text-xl font-semibold mb-2">No scheduled items yet</h3>
      <p className="text-muted-foreground mb-6 max-w-md mx-auto">
        Plan your content ahead of time. Schedule items from your library and track your content calendar.
      </p>
      <Button 
        onClick={() => setScheduleModalOpen(true)}
        className="gap-2"
        data-testid="schedule-from-empty"
      >
        <Plus className="w-4 h-4" />
        Schedule from Library
      </Button>
    </div>
  );

  return (
    <AppShell>
      <div className="p-5 sm:p-7 lg:p-10">
        <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-10">
          <div>
            <p className="text-[10px] text-primary/60 uppercase tracking-[0.2em] font-semibold mb-3">Scheduler</p>
            <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">
              Planning Scheduler
            </h1>
            <p className="text-zinc-500 text-sm mt-2">
              Plan and track your content schedule
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            <Button 
              variant="outline"
              onClick={() => setBatchModalOpen(true)}
              className="gap-2"
              data-testid="batch-schedule-btn"
            >
              <Settings2 className="w-4 h-4" />
              Advanced
            </Button>
            <Button 
              onClick={() => setScheduleModalOpen(true)}
              className="gap-2"
              data-testid="new-schedule-btn"
            >
              <Plus className="w-4 h-4" />
              Schedule Post
            </Button>
          </div>
        </div>
        
        {/* Content Calendar (Phase 3) */}
        <ContentCalendarView token={token} isFounder={user?.is_founder} onChanged={fetchScheduledItems} />

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="text-2xl font-bold">{statusCounts.all}</div>
            <div className="text-sm text-muted-foreground">Total Posts</div>
          </div>
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="text-2xl font-bold text-orange-400">{statusCounts.scheduled}</div>
            <div className="text-sm text-muted-foreground">Scheduled</div>
          </div>
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="text-2xl font-bold text-green-400">{statusCounts.completed}</div>
            <div className="text-sm text-muted-foreground">Completed</div>
          </div>
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="text-2xl font-bold text-red-400">{statusCounts.failed}</div>
            <div className="text-sm text-muted-foreground">Failed</div>
          </div>
        </div>
        
        {/* Filters & Search */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          {/* Status Filters */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2 sm:pb-0">
            {['all', 'scheduled', 'completed', 'failed'].map((status) => (
              <Button
                key={status}
                variant={activeFilter === status ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveFilter(status)}
                className="whitespace-nowrap capitalize"
                data-testid={`filter-${status}`}
              >
                {status === 'all' ? 'All' : status}
                <span className="ml-1.5 text-xs opacity-70">
                  ({statusCounts[status] || 0})
                </span>
              </Button>
            ))}
          </div>
          
          {/* Search */}
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search posts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 bg-card"
              data-testid="search-input"
            />
          </div>
        </div>
        
        {/* Content List */}
        {loading ? (
          <div className="grid gap-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-card border border-border rounded-xl p-4 animate-pulse">
                <div className="flex items-start gap-3">
                  <div className="w-14 h-14 bg-muted rounded-lg" />
                  <div className="flex-1">
                    <div className="h-4 bg-muted rounded w-1/2 mb-2" />
                    <div className="h-3 bg-muted rounded w-1/4" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="grid gap-4">
            {items.map((item) => (
              <ScheduledItemCard key={item.id} item={item} />
            ))}
          </div>
        )}
      </div>
      
      {/* Schedule Modal */}
      {scheduleModalOpen && (
        <ScheduleModal
          isOpen={scheduleModalOpen}
          onClose={() => setScheduleModalOpen(false)}
          platforms={platforms}
          timezones={timezones}
          token={token}
          onSuccess={() => {
            fetchScheduledItems();
            setScheduleModalOpen(false);
          }}
        />
      )}
      
      {/* Edit Modal */}
      {editModalOpen && selectedItem && (
        <EditModal
          isOpen={editModalOpen}
          onClose={() => {
            setEditModalOpen(false);
            setSelectedItem(null);
          }}
          item={selectedItem}
          platforms={platforms}
          timezones={timezones}
          token={token}
          onSuccess={() => {
            fetchScheduledItems();
            setEditModalOpen(false);
            setSelectedItem(null);
          }}
        />
      )}
      
      {/* Batch Schedule Modal */}
      {batchModalOpen && (
        <BatchScheduleModal
          isOpen={batchModalOpen}
          onClose={() => setBatchModalOpen(false)}
          platforms={platforms}
          timezones={timezones}
          token={token}
          onSuccess={() => {
            fetchScheduledItems();
            setBatchModalOpen(false);
          }}
        />
      )}
      </div>
    </AppShell>
  );
};

// Schedule Modal Component
const ScheduleModal = ({ isOpen, onClose, platforms, timezones, token, onSuccess }) => {
  const [step, setStep] = useState(1); // 1: Select content, 2: Schedule details
  const [selectedContentType, setSelectedContentType] = useState('video');
  const [libraryContent, setLibraryContent] = useState([]);
  const [loadingLibrary, setLoadingLibrary] = useState(false);
  const [selectedContent, setSelectedContent] = useState(null);
  
  // Schedule form
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [platform, setPlatform] = useState('instagram');
  const [timezone, setTimezone] = useState('America/Montreal');
  const [caption, setCaption] = useState('');
  const [notes, setNotes] = useState('');
  const [reminderEnabled, setReminderEnabled] = useState(true); // Default to enabled
  const [submitting, setSubmitting] = useState(false);

  // Fetch library content
  const fetchLibrary = async (type) => {
    try {
      setLoadingLibrary(true);
      const response = await axios.get(`${API}/api/library/?type=${type}&limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLibraryContent(response.data.items || []);
    } catch (error) {
      console.error('Failed to fetch library:', error);
    } finally {
      setLoadingLibrary(false);
    }
  };

  useEffect(() => {
    if (isOpen && step === 1) {
      fetchLibrary(selectedContentType);
    }
  }, [isOpen, selectedContentType, step]);

  // When content is selected, pre-fill caption
  useEffect(() => {
    if (selectedContent) {
      // Auto-fill caption from content metadata
      let autoCaption = '';
      if (selectedContent.type === 'video') {
        autoCaption = selectedContent.seo_caption || selectedContent.topic || '';
      } else if (selectedContent.type === 'image') {
        autoCaption = selectedContent.prompt || '';
      } else if (selectedContent.type === 'music') {
        autoCaption = `${selectedContent.genre || ''} - ${selectedContent.mood || ''}`.trim();
      }
      setCaption(autoCaption);
    }
  }, [selectedContent]);

  const handleSubmit = async () => {
    if (!selectedContent || !scheduledDate || !scheduledTime) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSubmitting(true);
      
      // Create ISO datetime
      const scheduledDateTime = new Date(`${scheduledDate}T${scheduledTime}`);
      
      await axios.post(`${API}/api/scheduler/`, {
        content_id: selectedContent.id,
        content_type: selectedContent.type,
        scheduled_time: scheduledDateTime.toISOString(),
        platform,
        timezone,
        caption: caption || null,
        notes: notes || null,
        reminder_enabled: reminderEnabled
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Content scheduled successfully! You\'ll get a reminder 30 minutes before.', {
        duration: 4000
      });
      onSuccess();
    } catch (error) {
      console.error('Failed to schedule:', error);
      toast.error(error.response?.data?.detail || 'Failed to schedule content');
    } finally {
      setSubmitting(false);
    }
  };

  const ContentIcon = contentTypeIcons[selectedContentType] || Video;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-5 border-b border-border flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">Schedule Content</h2>
            <p className="text-sm text-muted-foreground">
              {step === 1 ? 'Step 1: Select content from your library' : 'Step 2: Set schedule details'}
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0">
            <X className="w-4 h-4" />
          </Button>
        </div>
        
        {/* Content */}
        <div className="p-5 max-h-[60vh] overflow-y-auto">
          {step === 1 ? (
            <>
              {/* Content Type Tabs */}
              <div className="flex items-center gap-2 mb-4">
                {['video', 'image', 'music'].map((type) => {
                  const Icon = contentTypeIcons[type];
                  return (
                    <Button
                      key={type}
                      variant={selectedContentType === type ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => {
                        setSelectedContentType(type);
                        setSelectedContent(null);
                      }}
                      className="gap-1.5 capitalize"
                    >
                      <Icon className="w-4 h-4" />
                      {type}s
                    </Button>
                  );
                })}
              </div>
              
              {/* Library Grid */}
              {loadingLibrary ? (
                <div className="grid grid-cols-3 gap-3">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="aspect-square bg-muted rounded-lg animate-pulse" />
                  ))}
                </div>
              ) : libraryContent.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <ContentIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No {selectedContentType}s in your library</p>
                  <Button 
                    variant="link" 
                    onClick={() => window.location.href = `/studio/${selectedContentType}s`}
                    className="mt-2"
                  >
                    Create {selectedContentType}s
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                  {libraryContent.map((item) => (
                    <div
                      key={item.id}
                      onClick={() => setSelectedContent(item)}
                      className={`relative aspect-square rounded-lg overflow-hidden cursor-pointer border-2 transition-all ${
                        selectedContent?.id === item.id 
                          ? 'border-primary ring-2 ring-primary/30' 
                          : 'border-transparent hover:border-primary/50'
                      }`}
                      data-testid={`content-${item.id}`}
                    >
                      {item.thumbnail_url || item.image_url ? (
                        <img 
                          src={item.thumbnail_url || item.image_url}
                          alt={item.topic || item.prompt || 'Content'}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-muted flex items-center justify-center">
                          <ContentIcon className="w-8 h-8 text-muted-foreground" />
                        </div>
                      )}
                      {selectedContent?.id === item.id && (
                        <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                          <CheckCircle2 className="w-8 h-8 text-primary" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="space-y-4">
              {/* Selected Content Preview */}
              <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                {selectedContent?.thumbnail_url || selectedContent?.image_url ? (
                  <img 
                    src={selectedContent.thumbnail_url || selectedContent.image_url}
                    alt="Selected"
                    className="w-16 h-16 rounded-lg object-cover"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center">
                    <ContentIcon className="w-6 h-6 text-muted-foreground" />
                  </div>
                )}
                <div>
                  <p className="font-medium text-sm line-clamp-1">
                    {selectedContent?.topic || selectedContent?.prompt || 'Selected Content'}
                  </p>
                  <p className="text-xs text-muted-foreground capitalize">{selectedContent?.type}</p>
                </div>
              </div>
              
              {/* Date & Time */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1.5">Date *</label>
                  <Input
                    type="date"
                    value={scheduledDate}
                    onChange={(e) => setScheduledDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="bg-background"
                    data-testid="schedule-date"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5">Time *</label>
                  <Input
                    type="time"
                    value={scheduledTime}
                    onChange={(e) => setScheduledTime(e.target.value)}
                    className="bg-background"
                    data-testid="schedule-time"
                  />
                </div>
              </div>
              
              {/* Platform */}
              <div>
                <label className="block text-sm font-medium mb-1.5">Platform *</label>
                <select
                  value={platform}
                  onChange={(e) => setPlatform(e.target.value)}
                  className="w-full p-2.5 bg-background border border-border rounded-lg text-sm"
                  data-testid="schedule-platform"
                >
                  {platforms.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              
              {/* Timezone */}
              <div>
                <label className="block text-sm font-medium mb-1.5">Timezone</label>
                <select
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                  className="w-full p-2.5 bg-background border border-border rounded-lg text-sm"
                  data-testid="schedule-timezone"
                >
                  {timezones.map((tz) => (
                    <option key={tz} value={tz}>{tz.replace('_', ' ')}</option>
                  ))}
                </select>
              </div>
              
              {/* Caption */}
              <div>
                <label className="block text-sm font-medium mb-1.5">Caption (optional)</label>
                <textarea
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  placeholder="Enter caption for your post..."
                  rows={3}
                  className="w-full p-2.5 bg-background border border-border rounded-lg text-sm resize-none"
                  data-testid="schedule-caption"
                />
              </div>
              
              {/* Notes */}
              <div>
                <label className="block text-sm font-medium mb-1.5">Notes (optional)</label>
                <Input
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add a note for yourself..."
                  className="bg-background"
                  data-testid="schedule-notes"
                />
              </div>
              
              {/* Reminder Toggle */}
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Bell className="w-5 h-5 text-primary" />
                  <div>
                    <p className="text-sm font-medium">Reminder</p>
                    <p className="text-xs text-muted-foreground">Get notified 30 min before</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setReminderEnabled(!reminderEnabled)}
                  className={`relative w-11 h-6 rounded-full transition-colors ${
                    reminderEnabled ? 'bg-primary' : 'bg-muted-foreground/30'
                  }`}
                  data-testid="reminder-toggle"
                >
                  <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                    reminderEnabled ? 'left-6' : 'left-1'
                  }`} />
                </button>
              </div>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="p-5 border-t border-border flex items-center justify-between">
          <Button variant="ghost" onClick={step === 1 ? onClose : () => setStep(1)}>
            {step === 1 ? 'Cancel' : 'Back'}
          </Button>
          
          {step === 1 ? (
            <Button 
              onClick={() => setStep(2)}
              disabled={!selectedContent}
              className="gap-2"
            >
              Continue
              <ChevronRight className="w-4 h-4" />
            </Button>
          ) : (
            <Button 
              onClick={handleSubmit}
              disabled={submitting || !scheduledDate || !scheduledTime}
              className="gap-2"
              data-testid="submit-schedule"
            >
              {submitting ? 'Scheduling...' : 'Schedule Post'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// Edit Modal Component
const EditModal = ({ isOpen, onClose, item, platforms, timezones, token, onSuccess }) => {
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [platform, setPlatform] = useState(item.platform);
  const [timezone, setTimezone] = useState(item.timezone);
  const [caption, setCaption] = useState(item.caption || '');
  const [notes, setNotes] = useState(item.notes || '');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (item.scheduled_time) {
      const dt = new Date(item.scheduled_time);
      setScheduledDate(dt.toISOString().split('T')[0]);
      setScheduledTime(dt.toTimeString().slice(0, 5));
    }
  }, [item]);

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      
      const scheduledDateTime = new Date(`${scheduledDate}T${scheduledTime}`);
      
      await axios.put(`${API}/api/scheduler/${item.id}`, {
        scheduled_time: scheduledDateTime.toISOString(),
        platform,
        timezone,
        caption: caption || null,
        notes: notes || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Schedule updated!');
      onSuccess();
    } catch (error) {
      console.error('Failed to update:', error);
      toast.error(error.response?.data?.detail || 'Failed to update schedule');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const ContentIcon = contentTypeIcons[item.content_type] || Video;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-2xl w-full max-w-lg max-h-[90vh] overflow-hidden">
        <div className="p-5 border-b border-border flex items-center justify-between">
          <h2 className="text-lg font-semibold">Edit Schedule</h2>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0">
            <X className="w-4 h-4" />
          </Button>
        </div>
        
        <div className="p-5 space-y-4 max-h-[60vh] overflow-y-auto">
          {/* Content Preview */}
          <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
            {item.thumbnail_url ? (
              <img src={item.thumbnail_url} alt="" className="w-14 h-14 rounded-lg object-cover" />
            ) : (
              <div className="w-14 h-14 rounded-lg bg-muted flex items-center justify-center">
                <ContentIcon className="w-6 h-6 text-muted-foreground" />
              </div>
            )}
            <div>
              <p className="font-medium text-sm line-clamp-1">{item.title}</p>
              <p className="text-xs text-muted-foreground capitalize">{item.content_type}</p>
            </div>
          </div>
          
          {/* Date & Time */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1.5">Date *</label>
              <Input
                type="date"
                value={scheduledDate}
                onChange={(e) => setScheduledDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="bg-background"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Time *</label>
              <Input
                type="time"
                value={scheduledTime}
                onChange={(e) => setScheduledTime(e.target.value)}
                className="bg-background"
              />
            </div>
          </div>
          
          {/* Platform */}
          <div>
            <label className="block text-sm font-medium mb-1.5">Platform</label>
            <select
              value={platform}
              onChange={(e) => setPlatform(e.target.value)}
              className="w-full p-2.5 bg-background border border-border rounded-lg text-sm"
            >
              {platforms.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          
          {/* Caption */}
          <div>
            <label className="block text-sm font-medium mb-1.5">Caption</label>
            <textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Enter caption..."
              rows={3}
              className="w-full p-2.5 bg-background border border-border rounded-lg text-sm resize-none"
            />
          </div>
          
          {/* Notes */}
          <div>
            <label className="block text-sm font-medium mb-1.5">Notes</label>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add a note..."
              className="bg-background"
            />
          </div>
        </div>
        
        <div className="p-5 border-t border-border flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Batch Schedule Modal Component
const BatchScheduleModal = ({ isOpen, onClose, platforms, timezones, token, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [selectedContentType, setSelectedContentType] = useState('video');
  const [libraryContent, setLibraryContent] = useState([]);
  const [loadingLibrary, setLoadingLibrary] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  
  // Schedule settings
  const [pattern, setPattern] = useState('daily');
  const [startDate, setStartDate] = useState('');
  const [timeOfDay, setTimeOfDay] = useState('09:00');
  const [intervalDays, setIntervalDays] = useState(2);
  const [platform, setPlatform] = useState('instagram');
  const [timezone, setTimezone] = useState('America/Montreal');
  const [submitting, setSubmitting] = useState(false);

  const fetchLibrary = async (type) => {
    try {
      setLoadingLibrary(true);
      const response = await axios.get(`${API}/api/library/?type=${type}&limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLibraryContent(response.data.items || []);
    } catch (error) {
      console.error('Failed to fetch library:', error);
    } finally {
      setLoadingLibrary(false);
    }
  };

  useEffect(() => {
    if (isOpen && step === 1) {
      fetchLibrary(selectedContentType);
    }
  }, [isOpen, selectedContentType, step]);

  const toggleSelection = (item) => {
    setSelectedItems(prev => {
      const exists = prev.find(i => i.id === item.id);
      if (exists) {
        return prev.filter(i => i.id !== item.id);
      }
      return [...prev, item];
    });
  };

  const handleSubmit = async () => {
    if (selectedItems.length === 0) {
      toast.error('Please select at least one item');
      return;
    }
    if (!startDate || !timeOfDay) {
      toast.error('Please set start date and time');
      return;
    }

    try {
      setSubmitting(true);
      
      await axios.post(`${API}/api/scheduler/batch`, {
        content_ids: selectedItems.map(i => i.id),
        content_type: selectedContentType,
        platform,
        timezone,
        pattern,
        start_date: startDate,
        time_of_day: timeOfDay,
        interval_days: pattern === 'custom' ? intervalDays : null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Scheduled ${selectedItems.length} items!`);
      onSuccess();
    } catch (error) {
      console.error('Batch schedule failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to schedule items');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const ContentIcon = contentTypeIcons[selectedContentType] || Video;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="p-5 border-b border-border">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Settings2 className="w-5 h-5 text-primary" />
              Batch Schedule
            </h2>
            <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0">
              <X className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">
            Schedule multiple items at once with a repeating pattern
          </p>
        </div>
        
        <div className="p-5 max-h-[55vh] overflow-y-auto">
          {step === 1 ? (
            <>
              {/* Content Type Selection */}
              <div className="flex items-center gap-2 mb-4">
                {['video', 'image', 'music'].map((type) => {
                  const Icon = contentTypeIcons[type];
                  return (
                    <Button
                      key={type}
                      variant={selectedContentType === type ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => {
                        setSelectedContentType(type);
                        setSelectedItems([]);
                      }}
                      className="gap-1.5 capitalize"
                    >
                      <Icon className="w-4 h-4" />
                      {type}s
                    </Button>
                  );
                })}
              </div>
              
              <p className="text-sm text-muted-foreground mb-3">
                Select items to schedule ({selectedItems.length} selected)
              </p>
              
              {/* Library Grid */}
              {loadingLibrary ? (
                <div className="grid grid-cols-4 gap-3">
                  {[...Array(8)].map((_, i) => (
                    <div key={i} className="aspect-square bg-muted rounded-lg animate-pulse" />
                  ))}
                </div>
              ) : libraryContent.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <ContentIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No {selectedContentType}s available</p>
                </div>
              ) : (
                <div className="grid grid-cols-4 gap-3">
                  {libraryContent.map((item) => {
                    const isSelected = selectedItems.find(i => i.id === item.id);
                    return (
                      <div
                        key={item.id}
                        onClick={() => toggleSelection(item)}
                        className={`relative aspect-square rounded-lg overflow-hidden cursor-pointer border-2 transition-all ${
                          isSelected ? 'border-primary ring-2 ring-primary/30' : 'border-transparent hover:border-primary/50'
                        }`}
                      >
                        {item.thumbnail_url || item.image_url ? (
                          <img 
                            src={item.thumbnail_url || item.image_url}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-muted flex items-center justify-center">
                            <ContentIcon className="w-6 h-6 text-muted-foreground" />
                          </div>
                        )}
                        {isSelected && (
                          <div className="absolute top-1 right-1 w-5 h-5 bg-primary rounded-full flex items-center justify-center text-primary-foreground text-xs font-bold">
                            {selectedItems.findIndex(i => i.id === item.id) + 1}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            <div className="space-y-4">
              {/* Selected Items Summary */}
              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="text-sm font-medium mb-2">
                  Scheduling {selectedItems.length} {selectedContentType}s
                </p>
                <div className="flex flex-wrap gap-2">
                  {selectedItems.slice(0, 5).map((item, idx) => (
                    <div key={item.id} className="w-10 h-10 rounded bg-muted overflow-hidden">
                      {item.thumbnail_url || item.image_url ? (
                        <img src={item.thumbnail_url || item.image_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-xs">{idx + 1}</div>
                      )}
                    </div>
                  ))}
                  {selectedItems.length > 5 && (
                    <div className="w-10 h-10 rounded bg-muted flex items-center justify-center text-xs">
                      +{selectedItems.length - 5}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Schedule Pattern */}
              <div>
                <label className="block text-sm font-medium mb-2">Schedule Pattern</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { id: 'daily', label: 'Daily' },
                    { id: 'weekdays', label: 'Weekdays Only' },
                    { id: 'custom', label: 'Custom Interval' }
                  ].map((p) => (
                    <Button
                      key={p.id}
                      variant={pattern === p.id ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setPattern(p.id)}
                      className="text-xs"
                    >
                      {p.label}
                    </Button>
                  ))}
                </div>
                {pattern === 'custom' && (
                  <div className="mt-3 flex items-center gap-2">
                    <span className="text-sm">Every</span>
                    <Input
                      type="number"
                      value={intervalDays}
                      onChange={(e) => setIntervalDays(parseInt(e.target.value) || 1)}
                      min={1}
                      max={30}
                      className="w-20 bg-background"
                    />
                    <span className="text-sm">days</span>
                  </div>
                )}
              </div>
              
              {/* Start Date & Time */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1.5">Start Date *</label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="bg-background"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5">Time of Day *</label>
                  <Input
                    type="time"
                    value={timeOfDay}
                    onChange={(e) => setTimeOfDay(e.target.value)}
                    className="bg-background"
                  />
                </div>
              </div>
              
              {/* Platform */}
              <div>
                <label className="block text-sm font-medium mb-1.5">Platform</label>
                <select
                  value={platform}
                  onChange={(e) => setPlatform(e.target.value)}
                  className="w-full p-2.5 bg-background border border-border rounded-lg text-sm"
                >
                  {platforms.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              
              {/* Preview */}
              <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <p className="text-sm text-blue-400">
                  {pattern === 'daily' && `Will post ${selectedItems.length} items, one per day starting ${startDate}`}
                  {pattern === 'weekdays' && `Will post ${selectedItems.length} items on weekdays (Mon-Fri) starting ${startDate}`}
                  {pattern === 'custom' && `Will post ${selectedItems.length} items every ${intervalDays} days starting ${startDate}`}
                </p>
              </div>
            </div>
          )}
        </div>
        
        <div className="p-5 border-t border-border flex items-center justify-between">
          <Button variant="ghost" onClick={step === 1 ? onClose : () => setStep(1)}>
            {step === 1 ? 'Cancel' : 'Back'}
          </Button>
          
          {step === 1 ? (
            <Button 
              onClick={() => setStep(2)}
              disabled={selectedItems.length === 0}
              className="gap-2"
            >
              Continue ({selectedItems.length})
              <ChevronRight className="w-4 h-4" />
            </Button>
          ) : (
            <Button 
              onClick={handleSubmit}
              disabled={submitting || !startDate}
              className="gap-2"
              data-testid="submit-batch"
            >
              {submitting ? 'Scheduling...' : `Schedule ${selectedItems.length} Posts`}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SchedulerPage;
